import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import { Add, Save } from '@mui/icons-material';
import {
  addEditCancellationReasons,
  fetchAllcancellationReasons,
  deleteCancellationReasons,
} from "../../../actions/userCancellationActions";
import { arrayMoveImmutable } from "array-move";
import MainContentContainer from "../../MainContentContainer/MainContentContainer";
import SortableData from "./Sortable";

const CancellationReasonPage = () => {
  const dispatch = useDispatch();
  const cancellation_reasons = useSelector(state => state.cancellation_reasons.cancellation_reasons || []);
  const [cancellationReasons, setCancellationReasons] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    dispatch(fetchAllcancellationReasons());
  }, [dispatch]);

  useEffect(() => {
    if (cancellation_reasons.length) {
      setCancellationReasons(cancellation_reasons);
    }
  }, [cancellation_reasons]);

  const addNew = () => {
    const newItem = {
      reason: "",
      sort_order: cancellationReasons.length + 1,
      cancellation_id: `new-${Date.now()}` // Temporary ID for new items
    };
    setCancellationReasons(prev => [...prev, newItem]);
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const newItems = arrayMoveImmutable(cancellationReasons, oldIndex, newIndex);
    // Update local state immediately for smooth visual feedback
    setCancellationReasons(newItems);

    // Update sort order in Redux without resetting the visual state
    dispatch(addEditCancellationReasons(newItems))
      .then(() => {
        setSnackbar({
          open: true,
          message: 'Sort order updated successfully!',
          severity: 'success'
        });
      })
      .catch(() => {
        // If there's an error, revert to the previous state
        setCancellationReasons(cancellationReasons);
      });
  };

  const handleChange = (e, index) => {
    const value = e.target.value;
    setCancellationReasons(prev => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], reason: value };
      return newItems;
    });
  };

  const handleDelete = (e, ref_id, index) => {
    e.preventDefault();
    if (ref_id && !ref_id.toString().startsWith('new-')) {
      dispatch(deleteCancellationReasons(ref_id));
    }
    setCancellationReasons(prev => prev.filter((el, i) => i !== index));
  };

  const handleSubmit = () => {
    dispatch(addEditCancellationReasons(cancellationReasons))
      .then(() => {
        setSnackbar({
          open: true,
          message: 'Cancellation reasons saved successfully!',
          severity: 'success'
        });
        setIsInitialLoad(true); // Reset to allow fresh data load
      })
      .catch(() => {
        // Error is already handled in the action
      });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <MainContentContainer>
      <AppBar position="sticky">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Cancellation Reasons
          </Typography>
          <Button
            color="inherit"
            onClick={addNew}
            startIcon={<Add />}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Add New
          </Button>
          <Button
            color="inherit"
            onClick={handleSubmit}
            startIcon={<Save />}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Save
          </Button>
        </Toolbar>
      </AppBar>
      <Box sx={{ mt: 3 }}>
        <Paper sx={{ p: 2 }}>
          <SortableData
            items={cancellationReasons}
            onSortEnd={onSortEnd}
            onDelete={handleDelete}
            handleChange={handleChange}
          />
        </Paper>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainContentContainer>
  );
};

export default CancellationReasonPage;
